import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { validatePassword } from "@/utils/passwordValidation";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

type Step = "email" | "code" | "password" | "success";

const emailSchema = z.object({
  email: z.string().email("Please enter a valid email address."),
});

const codeSchema = z.object({
  code: z.string().length(6, "Please enter the 6-digit code."),
});

const passwordSchema = z
  .object({
    password: z
      .string()
      .min(8, "Password must be at least 8 characters.")
      .refine((val) => validatePassword(val).isValid, {
        message: "Password must include uppercase, lowercase, number, and special character.",
      }),
    confirmPassword: z.string().min(1, "Please confirm your password."),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match.",
    path: ["confirmPassword"],
  });

export default function PasswordResetDialog({
  open,
  onOpenChange,
  initialEmail,
  onInProgressChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialEmail?: string;
  onInProgressChange?: (inProgress: boolean) => void;
}) {
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [verifiedCode, setVerifiedCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  const dialogTitle = useMemo(() => {
    if (step === "email") return "Reset password";
    if (step === "code") return "Enter verification code";
    if (step === "password") return "Set new password";
    return "Password updated";
  }, [step]);

  const dialogDescription = useMemo(() => {
    if (step === "email") return "Enter your email address and we’ll send a 6-digit code.";
    if (step === "code") return email ? `We sent a 6-digit code to ${email}.` : "Enter the 6-digit code from your email.";
    if (step === "password") return "After the code is verified, set your new password below.";
    return "You can now sign in with your new password.";
  }, [step, email]);

  const emailForm = useForm<z.infer<typeof emailSchema>>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: initialEmail || "" },
  });

  const codeForm = useForm<z.infer<typeof codeSchema>>({
    resolver: zodResolver(codeSchema),
    defaultValues: { code: "" },
  });

  const passwordForm = useForm<z.infer<typeof passwordSchema>>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { password: "", confirmPassword: "" },
  });

  // Keep form email synced when dialog opens (e.g. prefilled from login form)
  useEffect(() => {
    if (!open) return;
    const next = (initialEmail || "").trim();
    if (next) emailForm.setValue("email", next);
  }, [open, initialEmail, emailForm]);

  useEffect(() => {
    onInProgressChange?.(open);

    if (!open) {
      // Reset UI state when closing
      setStep("email");
      setEmail("");
      setVerifiedCode(null);
      setError(null);
      setLoading(false);
      setResendCooldown(0);
      emailForm.reset({ email: initialEmail || "" });
      codeForm.reset({ code: "" });
      passwordForm.reset({ password: "", confirmPassword: "" });
    }
  }, [open, onInProgressChange, emailForm, codeForm, passwordForm, initialEmail]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const sendCode = async (emailAddress: string) => {
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.functions.invoke("send-password-reset-code", {
        body: { email: emailAddress },
      });

      if (error) {
        const msg = error.message?.toLowerCase?.() || "";
        if (msg.includes("rate")) {
          throw new Error("Too many requests. Please wait a few minutes and try again.");
        }
        throw new Error(error.message || "Failed to send verification code.");
      }

      if (data?.error) throw new Error(data.error);

      setEmail(emailAddress);
      setVerifiedCode(null);
      setStep("code");
      setResendCooldown(60);
      toast.success("Verification code sent to your email");
    } catch (e: any) {
      setError(e?.message || "Failed to send verification code.");
    } finally {
      setLoading(false);
    }
  };

  const onEmailSubmit = async (values: z.infer<typeof emailSchema>) => {
    await sendCode(values.email.trim());
  };

  const onResendCode = async () => {
    if (resendCooldown > 0) return;
    await sendCode(email);
  };

  const onCodeSubmit = async (values: z.infer<typeof codeSchema>) => {
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.functions.invoke("verify-password-reset-code", {
        body: { email, code: values.code },
      });

      if (error) {
        throw new Error(error.message || "Failed to verify code.");
      }

      if (!data?.success) {
        throw new Error(data?.error || "Incorrect code. Please check and try again.");
      }

      setVerifiedCode(values.code);
      setStep("password");
      toast.success("Code verified");
    } catch (e: any) {
      setError(e?.message || "Failed to verify code.");
    } finally {
      setLoading(false);
    }
  };

  const onPasswordSubmit = async (values: z.infer<typeof passwordSchema>) => {
    setLoading(true);
    setError(null);

    try {
      if (!verifiedCode) {
        throw new Error("Please verify your code first.");
      }

      const { data, error } = await supabase.functions.invoke("verify-password-reset-code", {
        body: { email, code: verifiedCode, newPassword: values.password },
      });

      if (error) {
        throw new Error(error.message || "Failed to update password.");
      }

      if (!data?.success || !data?.passwordUpdated) {
        throw new Error(data?.error || "Failed to update password.");
      }

      setStep("success");
      toast.success("Password updated successfully");
    } catch (e: any) {
      setError(e?.message || "Failed to update password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription>{dialogDescription}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {step === "email" && (
            <Card className="p-4">
              <Form {...emailForm}>
                <form onSubmit={emailForm.handleSubmit(onEmailSubmit)} className="space-y-4">
                  <FormField
                    control={emailForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email address</FormLabel>
                        <FormControl>
                          <Input type="email" autoComplete="email" placeholder="you@example.com" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? "Sending code…" : "Send code"}
                  </Button>

                  <p className="text-xs text-muted-foreground">
                    Note: email delivery can sometimes take a couple minutes depending on your mail provider.
                  </p>
                </form>
              </Form>
            </Card>
          )}

          {step === "code" && (
            <Card className="p-4">
              <Form {...codeForm}>
                <form onSubmit={codeForm.handleSubmit(onCodeSubmit)} className="space-y-6">
                  <FormField
                    control={codeForm.control}
                    name="code"
                    render={({ field }) => (
                      <FormItem className="flex flex-col items-center">
                        <FormLabel className="sr-only">Verification code</FormLabel>
                        <FormControl>
                          <InputOTP maxLength={6} value={field.value} onChange={field.onChange}>
                            <InputOTPGroup>
                              <InputOTPSlot index={0} />
                              <InputOTPSlot index={1} />
                              <InputOTPSlot index={2} />
                              <InputOTPSlot index={3} />
                              <InputOTPSlot index={4} />
                              <InputOTPSlot index={5} />
                            </InputOTPGroup>
                          </InputOTP>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="text-center text-sm text-muted-foreground">
                    Didn’t receive the code?{" "}
                    <button
                      type="button"
                      onClick={onResendCode}
                      disabled={resendCooldown > 0}
                      className="text-primary underline underline-offset-4 disabled:opacity-50 disabled:no-underline"
                    >
                      {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
                    </button>
                  </div>

                  <div className="space-y-2">
                    <Button type="submit" className="w-full" disabled={loading}>
                      {loading ? "Verifying…" : "Verify code"}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      className="w-full"
                      onClick={() => {
                        setStep("email");
                        setError(null);
                        codeForm.reset({ code: "" });
                      }}
                    >
                      Use a different email
                    </Button>
                  </div>
                </form>
              </Form>
            </Card>
          )}

          {step === "password" && (
            <Card className="p-4">
              <Form {...passwordForm}>
                <form onSubmit={passwordForm.handleSubmit(onPasswordSubmit)} className="space-y-4">
                  <FormField
                    control={passwordForm.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>New password</FormLabel>
                        <FormControl>
                          <PasswordInput autoComplete="new-password" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={passwordForm.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Confirm new password</FormLabel>
                        <FormControl>
                          <PasswordInput autoComplete="new-password" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="text-sm">
                    <p className="font-medium mb-1">Password must include:</p>
                    <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                      <li>At least 8 characters</li>
                      <li>At least one uppercase letter (A-Z)</li>
                      <li>At least one lowercase letter (a-z)</li>
                      <li>At least one number (0-9)</li>
                      <li>At least one special character (!@#$%^&*)</li>
                    </ul>
                  </div>

                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? "Updating…" : "Update password"}
                  </Button>
                </form>
              </Form>
            </Card>
          )}

          {step === "success" && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Your password is updated. You can now sign in.</p>
              <Button
                className="w-full"
                onClick={() => {
                  onOpenChange(false);
                }}
              >
                Back to sign in
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
